import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Layout from '../../component/layout';
import { Button } from 'primereact';
import 'primereact/resources/themes/lara-light-blue/theme.css';
import { getItem } from '../../../../utils/localStorage';
import { toastify } from '../../../../utils/toast';
import { makeRequest2 } from '../../../../utils/makeRequest';
import {
    getLevyTypes,
    addLevy,
    GET_CURRENCIES_URL,
    fetchFacilityPaymentDetails,
    getGLAccountsURL,
    getBankDetailsURL,
    getBillerAddressesURL,
    getDigitaxConfigURL
} from '../../../../utils/urls';

const INVOICE_DAY_OPTIONS = [
    { value: "5days", label: "5 days to end of month" },
    { value: "3days", label: "3 days to end of month" },
    { value: "1day", label: "1 day to end of month" },
    { value: "endmonth", label: "End of month" }
];

const DUE_DATE_OPTIONS = [
    { value: "1st", label: "1st of next month" },
    { value: "5th", label: "5th of next month" },
    { value: "10th", label: "10th of next month" }
];

const COLLECTION_FREQUENCY_OPTIONS = [
    { value: "Weekly", label: "Weekly" },
    { value: "Monthly", label: "Monthly" },
    { value: "Quarterly", label: "Quarterly" },
    { value: "Semi-annually", label: "Semi-annually" },
    { value: "Annually", label: "Annually" }
];

const LEVY_APPLICANT_OPTIONS = [
    { value: "home owner", label: "Home Owner" },
    { value: "tenant", label: "Tenant" }
];

const BILLING_TYPE_OPTIONS = [
    { value: "Prepaid", label: "Prepaid" },
    { value: "Postpaid", label: "Postpaid" }
];

const NOTIFICATION_TYPES = [
    { value: "SMS", label: "SMS" },
    { value: "EMAIL", label: "Email" }
];

const OVERDUE_REMINDER_OPTIONS = [
    { value: 1, label: "1 day after due date" },
    { value: 3, label: "3 days after due date" },
    { value: 7, label: "7 days after due date" }
];

const OVERDUE_REMINDER_MODES = [
    { value: "specific", label: "Specific Days After Due Date" },
    { value: "daily", label: "Daily After Due Date" }
];

const PENALTY_CATEGORY_OPTIONS = [
    { value: "financial", label: "Financial Penalty" },
    { value: "enforcement", label: "Enforcement Action" },
    { value: "both", label: "Both" }
];

const ENFORCEMENT_TYPE_OPTIONS = [
    { value: "water", label: "Water Supply Restriction" },
    { value: "electricity", label: "Electricity Supply Restriction" }
];

function AddLevy() {
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        levyName: '',
        levyType: '',
        amount: '',
        dueDate: '',
        levyApplicant: '',
        collectionFrequency: '',
        invoiceDay: '',
        billingType: 'Postpaid',
        // Payment fields
        currency: '',
        mobilePayment: false,
        paymentMethodId: '',
        bankPayment: false,
        digitaxEnabled: false,
        digitaxProfileId: null,
        bankAccountId: '', // Changed from bankDetails to bankAccountId for dropdown selection
        // Biller address field
        billerAddressId: '', // New field for biller address selection
        // GL Account fields (moved from contract)
        glAccounts: {
            invoice: {
                debit: '',
                credit: ''
            },
            payment: {
                debit: '',
                credit: ''
            }
        },
        reminder: {
            name: '',
            type: 'standard',
            module: 'levy',
            time: '09:00',
            notificationTypes: ['SMS', 'EMAIL'],
            remindOn: {
                invoiceDate: true,
                dueDate: false,
                afterOverdue: {
                    enabled: false,
                    daily: false,
                    days: []
                }
            },
            message: ''
        },
        penalty: {
            name: '',
            category: '',
            type: '',
            enforcementType: '',
            effectDays: '',
            percentage: '',
            amount: '',
            isActive: true
        }
    });

    // State variables
    const [currencies, setCurrencies] = useState([]);
    const [paymentSettings, setPaymentSettings] = useState([]);
    const [bankAccounts, setBankAccounts] = useState([]); // New state for bank accounts
    const [billerAddresses, setBillerAddresses] = useState([]); // New state for biller addresses
    const [glAccounts, setGlAccounts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingGLAccounts, setLoadingGLAccounts] = useState(false);
    const [loadingBankAccounts, setLoadingBankAccounts] = useState(false); // New loading state
    const [loadingBillerAddresses, setLoadingBillerAddresses] = useState(false); // New loading state
    const [levyTypes, setLevyTypes] = useState([]);

    const [digitaxProfiles, setDigitaxProfiles] = useState([]);

    const fetchDigitaxProfiles = async () => {
        try {
            const facilityId = await getItem("selectedFacilityId");
            const response = await makeRequest2(
                `${getDigitaxConfigURL}/${facilityId}`,
                "GET"
            );

            console.log("Digitax config response:", response);

            if (response.success) {
                setDigitaxProfiles(
                    response.data?.digitax?.profiles || []
                );
            }
        } catch (error) {
            console.error(
                "Failed to fetch DigiTax profiles:",
                error
            );
        }
    };

    const handleInputChange = (field, value) => {
        const updateNestedField = (prev, path, newValue) => {
            const keys = path.split('.');
            const updatedState = { ...prev };
            let current = updatedState;

            for (let i = 0; i < keys.length - 1; i++) {
                current = current[keys[i]];
            }

            current[keys[keys.length - 1]] = newValue;
            return updatedState;
        };

        setFormData(prev => {
            // Special handling for specific fields
            if (field === 'reminder.remindOn.dueDate') {
                return updateNestedField(prev, 'reminder.remindOn.dueDate', !!value);
            }

            if (field === 'reminder.remindOn.invoiceDate') {
                return updateNestedField(prev, 'reminder.remindOn.invoiceDate', !!value);
            }

            if (field === 'reminder.remindOn.afterOverdue.enabled') {
                return updateNestedField(prev, 'reminder', {
                    ...prev.reminder,
                    remindOn: {
                        ...prev.reminder.remindOn,
                        afterOverdue: {
                            enabled: !!value,
                            daily: false,
                            days: []
                        }
                    }
                });
            }

            if (field === 'reminder.remindOn.afterOverdue.mode') {
                const isDaily = value === 'daily';
                return updateNestedField(prev, 'reminder', {
                    ...prev.reminder,
                    remindOn: {
                        ...prev.reminder.remindOn,
                        afterOverdue: {
                            ...prev.reminder.remindOn.afterOverdue,
                            daily: isDaily,
                            days: isDaily ? [] : prev.reminder.remindOn.afterOverdue.days
                        }
                    }
                });
            }

            if (field === 'reminder.remindOn.afterOverdue.days') {
                return updateNestedField(prev, 'reminder.remindOn.afterOverdue.days',
                    Array.isArray(value) ? value : [value].filter(Boolean)
                );
            }

            // Special handling for penalty category
            if (field === 'penalty.category') {
                const newState = { ...prev };
                newState.penalty.category = value;

                if (value !== 'financial' && value !== 'both') {
                    newState.penalty.type = '';
                    newState.penalty.percentage = '';
                    newState.penalty.amount = '';
                }

                if (value !== 'enforcement' && value !== 'both') {
                    newState.penalty.enforcementType = '';
                }

                return newState;
            }

            // Handle nested and top-level fields
            if (field.includes('.')) {
                return updateNestedField(prev, field, value);
            }

            // Handle top-level fields
            return {
                ...prev,
                [field]: value
            };
        });
    };

    const handleNotificationTypeChange = (type) => {
        setFormData(prev => {
            const types = prev.reminder.notificationTypes;
            const newTypes = types.includes(type)
                ? types.filter(t => t !== type)
                : [...types, type];

            return {
                ...prev,
                reminder: {
                    ...prev.reminder,
                    notificationTypes: newTypes
                }
            };
        });
    };

    const clearForm = () => {
        setFormData({
            levyName: '',
            levyType: '',
            amount: '',
            dueDate: '',
            levyApplicant: '',
            collectionFrequency: '',
            invoiceDay: '',
            billingType: 'Postpaid',
            currency: '',
            mobilePayment: false,
            paymentMethodId: '',
            bankPayment: false,
            digitaxEnabled: false,
            bankAccountId: '',
            billerAddressId: '',
            glAccounts: {
                invoice: {
                    debit: '',
                    credit: ''
                },
                payment: {
                    debit: '',
                    credit: ''
                }
            },
            reminder: {
                name: '',
                type: 'standard',
                module: 'levy',
                time: '09:00',
                notificationTypes: ['SMS', 'EMAIL'],
                remindOn: {
                    invoiceDate: true,
                    dueDate: false,
                    afterOverdue: {
                        enabled: false,
                        daily: false,
                        days: []
                    }
                },
                message: ''
            },
            penalty: {
                name: '',
                category: '',
                type: '',
                enforcementType: '',
                effectDays: '',
                percentage: '',
                amount: '',
                isActive: true
            }
        });
    };

    const fetchLevyTypes = async () => {
        try {
            const facilityId = await getItem('selectedFacilityId');
            const response = await makeRequest2(getLevyTypes + '/' + facilityId, 'GET', {});

            if (response.success) {
                const levyTypesData = Array.isArray(response.data) ? response.data :
                    (response.data?.data ? response.data.data : []);
                setLevyTypes(levyTypesData);
            } else {
                throw new Error(response.error);
            }
        } catch (err) {
            console.error('Error fetching levy types:', err);
            toastify(err.message, 'error');
        }
    };

    // Fetch currencies
    const fetchCurrencies = async () => {
        try {
            const facilityId = await getItem("selectedFacilityId");
            const response = await makeRequest2(`${GET_CURRENCIES_URL}/${facilityId}`, "GET");

            if (response.success) {
                let currencyData = [];

                if (Array.isArray(response.data)) {
                    currencyData = response.data;
                } else if (response.data?.currencies && Array.isArray(response.data.currencies)) {
                    currencyData = response.data.currencies;
                } else if (response.data?.data && Array.isArray(response.data.data)) {
                    currencyData = response.data.data;
                } else if (typeof response.data === 'object' && response.data !== null) {
                    currencyData = [response.data];
                }

                const processedCurrencies = currencyData.map(curr => ({
                    _id: curr._id,
                    code: curr.code || 'Unknown',
                    name: curr.name || curr.currencyName || 'Unknown Currency'
                }));

                setCurrencies(processedCurrencies);
            } else {
                throw new Error(response.error || "Failed to fetch currencies");
            }
        } catch (error) {
            console.error('Error fetching currencies:', error);
            toastify(error.message || "Error fetching currencies", "error");
            setCurrencies([]);
        }
    };

    // Fetch payment settings
    const fetchPaymentSettings = async () => {
        try {
            const facilityId = await getItem("selectedFacilityId");
            const url = `${fetchFacilityPaymentDetails}/${facilityId}`;
            const response = await makeRequest2(url, "GET");

            if (response.success && response.data?.length > 0) {
                const formattedData = response.data.map(item => ({
                    shortCode: item.shortCode,
                    module: item.module,
                    _id: item._id
                }));
                setPaymentSettings(formattedData);
            } else {
                setPaymentSettings([]);
            }
        } catch (error) {
            console.error("Fetch error:", error);
            toastify("Error fetching payment details", "error");
            setPaymentSettings([]);
        }
    };

    // Fetch bank accounts
    const fetchBankAccounts = async () => {
        try {
            setLoadingBankAccounts(true);
            const facilityId = await getItem("selectedFacilityId");
            const response = await makeRequest2(`${getBankDetailsURL}/${facilityId}`, "GET");

            if (response.success) {
                // Handle the response structure based on your backend
                const bankData = response.data?.data || response.data || [];
                setBankAccounts(bankData);
            } else {
                console.error("Failed to fetch bank details:", response.error);
                setBankAccounts([]);
                toastify(response.error || "Failed to fetch bank details", "error");
            }
        } catch (error) {
            console.error("Error fetching bank accounts:", error);
            toastify("Error fetching bank accounts", "error");
            setBankAccounts([]);
        } finally {
            setLoadingBankAccounts(false);
        }
    };

    // Fetch biller addresses (new function)
    const fetchBillerAddresses = async () => {
        try {
            setLoadingBillerAddresses(true);
            const facilityId = await getItem("selectedFacilityId");
            const response = await makeRequest2(`${getBillerAddressesURL}/${facilityId}`, "GET");

            if (response.success) {
                // Handle the response structure based on your backend
                const billerData = response.data?.data || response.data || [];
                setBillerAddresses(billerData);
            } else {
                console.error("Failed to fetch biller addresses:", response.error);
                setBillerAddresses([]);
                toastify(response.error || "Failed to fetch biller addresses", "error");
            }
        } catch (error) {
            console.error("Error fetching biller addresses:", error);
            toastify("Error fetching biller addresses", "error");
            setBillerAddresses([]);
        } finally {
            setLoadingBillerAddresses(false);
        }
    };

    // Fetch GL accounts
    const fetchGLAccounts = async () => {
        try {
            setLoadingGLAccounts(true);
            const facilityId = await getItem("selectedFacilityId");

            if (!facilityId) {
                console.error("No facility ID found in localStorage");
                toastify("No facility selected. Please select a facility first.", "error");
                return;
            }

            const response = await makeRequest2(`${getGLAccountsURL}/${facilityId}`, "GET");

            if (response && response.success) {
                const accountsData = response.data?.data || [];
                setGlAccounts(accountsData);
            } else {
                const errorMsg = response?.message || "Failed to fetch GL accounts.";
                console.error("GL accounts API error:", errorMsg);
                toastify(errorMsg, "error");
                setGlAccounts([]);
            }
        } catch (error) {
            console.error("Error in fetchGLAccounts:", error);
            toastify("Error fetching GL accounts. Please try again later.", "error");
            setGlAccounts([]);
        } finally {
            setLoadingGLAccounts(false);
        }
    };

    const handleLevySubmit = async () => {
        try {
            setLoading(true);

            // Enhanced form validation (keep your existing validation logic)
            const validateForm = () => {
                const {
                    levyName,
                    levyType,
                    amount,
                    levyApplicant,
                    collectionFrequency,
                    invoiceDay,
                    dueDate,
                    billingType,
                    reminder,
                    penalty,
                    mobilePayment,
                    paymentMethodId,
                    bankPayment,
                    digitaxEnabled,
                    digitaxProfileId,
                    bankAccountId,
                    billerAddressId,
                    currency,
                    glAccounts
                } = formData;

                // Validate core levy fields
                if (!levyName || levyName.trim().length === 0) {
                    throw new Error('Levy name is required and cannot be empty');
                }
                if (levyName.trim().length > 100) {
                    throw new Error('Levy name cannot exceed 100 characters');
                }
                if (!levyType) throw new Error('Levy type is required');
                if (!amount || isNaN(parseFloat(amount)) || parseFloat(amount) <= 0) {
                    throw new Error('Amount must be a positive number');
                }
                if (parseFloat(amount) > 999999999) {
                    throw new Error('Amount cannot exceed 999,999,999');
                }
                if (!levyApplicant) throw new Error('Levy applicant is required');
                if (!collectionFrequency) throw new Error('Collection frequency is required');
                if (!invoiceDay) throw new Error('Invoice day is required');
                if (!dueDate) throw new Error('Due date is required');
                if (!billingType) throw new Error('Billing type is required');

                // Validate payment fields
                if (!currency) throw new Error('Currency is required');
                if (mobilePayment && !paymentMethodId) {
                    throw new Error('Payment method is required when mobile payment is enabled');
                }
                if (bankPayment && !bankAccountId) {
                    throw new Error('Bank account is required when bank payment is enabled');
                }

                // Ensure at least one payment method is enabled
                if (!mobilePayment && !bankPayment) {
                    throw new Error('At least one payment method (Mobile or Bank) must be enabled');
                }

                if (digitaxProfileId && !digitaxProfiles.some(
                    profile =>
                        profile._id === digitaxProfileId &&
                        profile.enabled
                )) {
                    throw new Error(
                        "Selected DigiTax profile is invalid or disabled"
                    );
                }

                // Validate biller address
                if (!billerAddressId) throw new Error('Biller address is required');

                // Validate reminder settings
                if (!reminder.notificationTypes || reminder.notificationTypes.length === 0) {
                    throw new Error('At least one notification type is required');
                }

                // Validate overdue reminder settings
                if (reminder.remindOn.afterOverdue.enabled) {
                    if (!reminder.remindOn.afterOverdue.daily &&
                        (!reminder.remindOn.afterOverdue.days || reminder.remindOn.afterOverdue.days.length === 0)) {
                        throw new Error('Please select at least one day for overdue reminders or enable daily reminders');
                    }
                }

                // Validate penalty settings if category is selected
                if (penalty?.category) {
                    if (!penalty.name || penalty.name.trim().length === 0) {
                        throw new Error('Penalty name is required');
                    }
                    if (penalty.name.trim().length > 100) {
                        throw new Error('Penalty name cannot exceed 100 characters');
                    }

                    if (!penalty.effectDays || isNaN(parseInt(penalty.effectDays)) || parseInt(penalty.effectDays) < 1) {
                        throw new Error('Effect days must be at least 1');
                    }
                    if (parseInt(penalty.effectDays) > 365) {
                        throw new Error('Effect days cannot exceed 365 days');
                    }

                    if (['financial', 'both'].includes(penalty.category)) {
                        if (!penalty.type) {
                            throw new Error('Financial penalty type is required');
                        }

                        if (penalty.type === 'fixed') {
                            if (!penalty.amount || isNaN(parseFloat(penalty.amount)) || parseFloat(penalty.amount) <= 0) {
                                throw new Error('Penalty amount must be a positive number for fixed penalty type');
                            }
                            if (parseFloat(penalty.amount) > 999999999) {
                                throw new Error('Penalty amount cannot exceed 999,999,999');
                            }
                        }

                        if (penalty.type === 'percentage') {
                            if (!penalty.percentage || isNaN(parseFloat(penalty.percentage)) ||
                                parseFloat(penalty.percentage) <= 0 || parseFloat(penalty.percentage) > 100) {
                                throw new Error('Penalty percentage must be between 1 and 100');
                            }
                        }
                    }

                    if (['enforcement', 'both'].includes(penalty.category) && !penalty.enforcementType) {
                        throw new Error('Enforcement type is required for enforcement penalties');
                    }
                }

                const hasReminderEnabled = reminder.remindOn.invoiceDate ||
                    reminder.remindOn.dueDate ||
                    reminder.remindOn.afterOverdue.enabled;

                if (!hasReminderEnabled) {
                    throw new Error('At least one reminder type must be enabled (Invoice Date, Due Date, or Overdue)');
                }

                if (reminder.message && reminder.message.length > 500) {
                    throw new Error('Reminder message cannot exceed 500 characters');
                }

                if (reminder.time && !/^([01]?[0-9]|2[0-3]):[0-5][0-9]$/.test(reminder.time)) {
                    throw new Error('Reminder time must be in HH:MM format');
                }
            };

            // Run validation
            validateForm();

            const facilityId = await getItem('selectedFacilityId');
            if (!facilityId) {
                throw new Error('No facility selected. Please select a facility first.');
            }

            // Prepare reminder payload
            const reminderPayload = {
                name: `${formData.levyName.trim()} Reminder`,
                type: 'standard',
                module: 'levy',
                time: formData.reminder.time || '09:00',
                notificationTypes: formData.reminder.notificationTypes || ['SMS', 'EMAIL'],
                remindOn: {
                    invoiceDate: !!formData.reminder.remindOn.invoiceDate,
                    dueDate: !!formData.reminder.remindOn.dueDate,
                    afterOverdue: {
                        enabled: !!formData.reminder.remindOn.afterOverdue.enabled,
                        daily: !!formData.reminder.remindOn.afterOverdue.daily,
                        days: formData.reminder.remindOn.afterOverdue.days || []
                    }
                },
                message: formData.reminder.message?.trim() || ''
            };

            // Prepare penalty payload (only if category is selected)
            const penaltyPayload = formData.penalty.category ? {
                name: formData.penalty.name.trim(),
                category: formData.penalty.category,
                type: ['financial', 'both'].includes(formData.penalty.category) ? formData.penalty.type : null,
                percentage: ['financial', 'both'].includes(formData.penalty.category) &&
                    formData.penalty.type === 'percentage' ? parseFloat(formData.penalty.percentage) : null,
                amount: ['financial', 'both'].includes(formData.penalty.category) &&
                    formData.penalty.type === 'fixed' ? parseFloat(formData.penalty.amount) : null,
                enforcementType: ['enforcement', 'both'].includes(formData.penalty.category) ?
                    formData.penalty.enforcementType : null,
                effectDays: parseInt(formData.penalty.effectDays),
                isActive: formData.penalty.isActive !== false
            } : null;

            // Prepare main payload
            const payload = {
                levyName: formData.levyName.trim(),
                levyType: formData.levyType,
                amount: parseFloat(formData.amount),
                dueDate: formData.dueDate,
                levyApplicant: formData.levyApplicant,
                collectionFrequency: formData.collectionFrequency,
                invoiceDay: formData.invoiceDay,
                billingType: formData.billingType,
                currency: formData.currency,
                mobilePayment: !!formData.mobilePayment,
                paymentMethodId: formData.mobilePayment ? formData.paymentMethodId : null,
                bankPayment: !!formData.bankPayment,
                digitaxEnabled: Boolean(
                    formData.digitaxProfileId || formData.digitaxEnabled
                ),
                digitaxProfileId: formData.digitaxProfileId || null,
                bankAccountId: formData.bankPayment ? formData.bankAccountId : null,
                billerAddressId: formData.billerAddressId,
                glAccounts: {
                    invoice: {
                        debit: formData.glAccounts.invoice.debit,
                        credit: formData.glAccounts.invoice.credit
                    },
                    payment: {
                        debit: formData.glAccounts.payment.debit,
                        credit: formData.glAccounts.payment.credit
                    }
                },
                reminder: reminderPayload,
                penalty: penaltyPayload
            };

            // Make the API request
            const response = await makeRequest2(addLevy + '/' + facilityId, 'POST', payload);

            // CLEAN SUCCESS/ERROR HANDLING
            if (response && response.success === true) {
                // Success case (now handles both 200 and 201 status codes)
                const successMessage = response.data?.message || 'Levy added successfully with all configurations';
                toastify(successMessage, 'success');

                clearForm();
                navigate("/facility/levy_management");

            } else {
                // Error case - extract error message
                let errorMessage = 'Failed to add levy';

                if (response && response.error) {
                    errorMessage = response.error;

                    // Clean up error messages that include HTTP status codes
                    if (errorMessage.includes('Error: 400 -') || errorMessage.includes('Error: 500 -')) {
                        const parts = errorMessage.split(' - ');
                        if (parts.length > 1) {
                            errorMessage = parts.slice(1).join(' - ');
                        }
                    }
                } else if (response && response.message) {
                    errorMessage = response.message;
                } else if (typeof response === 'string') {
                    errorMessage = response;
                }

                console.error('Levy submission failed:', response);
                throw new Error(errorMessage);
            }

        } catch (err) {
            console.error('Error in handleLevySubmit:', err);

            let displayMessage = err.message || 'An unexpected error occurred';

            // Clean up error messages that might include HTTP status codes
            if (displayMessage.includes('Error: 400 -') || displayMessage.includes('Error: 201 -')) {
                // Extract the actual error message after the status code
                const parts = displayMessage.split(' - ');
                if (parts.length > 1) {
                    displayMessage = parts.slice(1).join(' - ');
                }
            }

            // Handle specific error cases
            if (displayMessage.includes('already exists')) {
                // Keep the duplicate levy message as is
            } else if (displayMessage.includes('401')) {
                displayMessage = 'Authentication failed. Please log in again.';
            } else if (displayMessage.includes('403')) {
                displayMessage = 'You do not have permission to perform this action.';
            } else if (displayMessage.includes('404')) {
                displayMessage = 'The requested resource was not found. Please try again.';
            } else if (displayMessage.includes('500')) {
                displayMessage = 'Server error occurred. Please try again later.';
            } else if (displayMessage.includes('Network')) {
                displayMessage = 'Network error. Please check your connection and try again.';
            } else if (displayMessage.includes('timeout')) {
                displayMessage = 'Request timed out. Please try again.';
            }

            toastify(displayMessage, 'error');

        } finally {
            setLoading(false);
        }
    };

    // Optional: Add a helper function to validate individual fields on blur
    const validateField = (fieldName, value) => {
        switch (fieldName) {
            case 'levyName':
                if (!value || value.trim().length === 0) {
                    return 'Levy name is required';
                }
                if (value.trim().length > 100) {
                    return 'Levy name cannot exceed 100 characters';
                }
                break;
            case 'amount':
                if (!value || isNaN(parseFloat(value)) || parseFloat(value) <= 0) {
                    return 'Amount must be a positive number';
                }
                if (parseFloat(value) > 999999999) {
                    return 'Amount cannot exceed 999,999,999';
                }
                break;
            case 'penalty.effectDays':
                if (value && (isNaN(parseInt(value)) || parseInt(value) < 1)) {
                    return 'Effect days must be at least 1';
                }
                if (value && parseInt(value) > 365) {
                    return 'Effect days cannot exceed 365 days';
                }
                break;
            case 'penalty.amount':
                if (value && (isNaN(parseFloat(value)) || parseFloat(value) <= 0)) {
                    return 'Penalty amount must be a positive number';
                }
                if (value && parseFloat(value) > 999999999) {
                    return 'Penalty amount cannot exceed 999,999,999';
                }
                break;
            case 'penalty.percentage':
                if (value && (isNaN(parseFloat(value)) || parseFloat(value) <= 0 || parseFloat(value) > 100)) {
                    return 'Penalty percentage must be between 1 and 100';
                }
                break;
            default:
                return null;
        }
        return null;
    };

    useEffect(() => {
        fetchDigitaxProfiles();
        fetchLevyTypes();
        fetchCurrencies();
        fetchPaymentSettings();
        fetchBankAccounts(); // New fetch call
        fetchBillerAddresses(); // New fetch call
        fetchGLAccounts();
    }, []);

    // Helper function to determine if financial penalty inputs should be shown
    const showFinancialPenaltyInputs = () => {
        return ['financial', 'both'].includes(formData.penalty.category);
    };

    // Helper function to determine if enforcement penalty inputs should be shown
    const showEnforcementPenaltyInputs = () => {
        return ['enforcement', 'both'].includes(formData.penalty.category);
    };

    return (
        <Layout>
            <div className="page-header">
                <div className="page-block">
                    <div className="row align-items-center">
                        <div className="col-md-12">
                            <ul className="breadcrumb">
                                <li className="breadcrumb-item">
                                    <Link to="/facility/">Dashboard</Link>
                                </li>
                                <li className="breadcrumb-item">
                                    <Link to="/facility/levy_management">Levy Management</Link>
                                </li>
                                <li className="breadcrumb-item">Add New Levy</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>

            <div className="row">
                <div className="col-sm-12">
                    <div className="card">
                        <div className="card-header">
                            <Link to="#" onClick={() => navigate(-1)}>
                                <span><i className="ti ti-arrow-narrow-left"></i> Back</span>
                            </Link>
                        </div>
                    </div>

                    <div className="card">
                        <div className="card-header">
                            <h5>Add New Levy</h5>
                        </div>
                        <div className='card-body'>
                            <div className='container'>
                                <div className='row'>
                                    {/* Basic Levy Information */}
                                    <div className="col-sm-6">
                                        <div className="mb-3">
                                            <label className="form-label">Levy Name<span className="text-danger">*</span></label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={formData.levyName}
                                                onChange={(e) => handleInputChange('levyName', e.target.value)}
                                            />
                                        </div>
                                    </div>

                                    <div className="col-sm-6">
                                        <div className="mb-3">
                                            <label className="form-label">Levy Type<span className="text-danger">*</span></label>
                                            <select
                                                className="form-control"
                                                value={formData.levyType}
                                                onChange={(e) => handleInputChange('levyType', e.target.value)}
                                            >
                                                <option value="">-- Select --</option>
                                                {levyTypes.map((type) => (
                                                    <option key={type._id} value={type._id}>
                                                        {type.name}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>

                                    <div className="col-sm-6">
                                        <div className="mb-3">
                                            <label className="form-label">Amount<span className="text-danger">*</span></label>
                                            <input
                                                type="number"
                                                className="form-control"
                                                value={formData.amount}
                                                onChange={(e) => handleInputChange('amount', e.target.value)}
                                            />
                                        </div>
                                    </div>

                                    <div className="col-sm-6">
                                        <div className="mb-3">
                                            <label className="form-label">Currency<span className="text-danger">*</span></label>
                                            <select
                                                className="form-control"
                                                value={formData.currency}
                                                onChange={(e) => handleInputChange('currency', e.target.value)}
                                            >
                                                <option value="">-- Select Currency --</option>
                                                {Array.isArray(currencies) && currencies.length > 0 ? (
                                                    currencies.map((curr) => (
                                                        <option key={curr._id} value={curr._id}>
                                                            {curr.name}
                                                        </option>
                                                    ))
                                                ) : (
                                                    <option value="" disabled>No currencies available</option>
                                                )}
                                            </select>
                                        </div>
                                    </div>

                                    {/* Billing Type */}
                                    <div className="col-sm-6">
                                        <div className="mb-3">
                                            <label className="form-label">Billing Type<span className="text-danger">*</span></label>
                                            <select
                                                className="form-control"
                                                value={formData.billingType}
                                                onChange={(e) => handleInputChange('billingType', e.target.value)}
                                            >
                                                {BILLING_TYPE_OPTIONS.map(option => (
                                                    <option key={option.value} value={option.value}>
                                                        {option.label}
                                                    </option>
                                                ))}
                                            </select>
                                            <small className="text-muted">
                                                {formData.billingType === 'Prepaid'
                                                    ? 'Customers pay before services are provided'
                                                    : 'Customers pay after services are provided'
                                                }
                                            </small>
                                        </div>
                                    </div>

                                    {/* Biller Address */}
                                    <div className="col-sm-6">
                                        <div className="mb-3">
                                            <label className="form-label">Biller Address<span className="text-danger">*</span></label>
                                            <select
                                                className="form-control"
                                                value={formData.billerAddressId}
                                                onChange={(e) => handleInputChange('billerAddressId', e.target.value)}
                                                disabled={loadingBillerAddresses}
                                            >
                                                <option value="">-- Select Biller Address --</option>
                                                {billerAddresses.map((address) => (
                                                    <option key={address._id} value={address._id}>
                                                        {address.name} - {address.companyName}
                                                        {address.isDefault && ' (Default)'}
                                                    </option>
                                                ))}
                                            </select>
                                            {loadingBillerAddresses && <small className="text-muted">Loading addresses...</small>}
                                            {!loadingBillerAddresses && billerAddresses.length === 0 && (
                                                <small className="text-danger">No biller addresses found. Please add one in facility settings.</small>
                                            )}
                                        </div>
                                    </div>

                                    <div className="col-sm-6">
                                        <div className="mb-3">
                                            <label className="form-label">Collection Frequency<span className="text-danger">*</span></label>
                                            <select
                                                className="form-control"
                                                value={formData.collectionFrequency}
                                                onChange={(e) => handleInputChange('collectionFrequency', e.target.value)}
                                            >
                                                <option value="">Select</option>
                                                {COLLECTION_FREQUENCY_OPTIONS.map(option => (
                                                    <option key={option.value} value={option.value}>
                                                        {option.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>

                                    <div className="col-sm-6">
                                        <div className="mb-3">
                                            <label className="form-label">
                                                Levy Applicant<span className="text-danger">*</span> <em>(who pays)</em>
                                            </label>
                                            <select
                                                className="form-control"
                                                value={formData.levyApplicant}
                                                onChange={(e) => handleInputChange('levyApplicant', e.target.value)}
                                            >
                                                <option value="">Select</option>
                                                {LEVY_APPLICANT_OPTIONS.map(option => (
                                                    <option key={option.value} value={option.value}>
                                                        {option.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>

                                    <div className="col-sm-6">
                                        <div className="mb-3">
                                            <label className="form-label">Invoice Day<span className="text-danger">*</span></label>
                                            <select
                                                className="form-control"
                                                value={formData.invoiceDay}
                                                onChange={(e) => handleInputChange('invoiceDay', e.target.value)}
                                            >
                                                <option value="">Select</option>
                                                {INVOICE_DAY_OPTIONS.map(option => (
                                                    <option key={option.value} value={option.value}>
                                                        {option.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>

                                    <div className="col-sm-6">
                                        <div className="mb-3">
                                            <label className="form-label">Due Date<span className="text-danger">*</span></label>
                                            <select
                                                className="form-control"
                                                value={formData.dueDate}
                                                onChange={(e) => handleInputChange('dueDate', e.target.value)}
                                            >
                                                <option value="">Select</option>
                                                {DUE_DATE_OPTIONS.map(option => (
                                                    <option key={option.value} value={option.value}>
                                                        {option.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>

                                    {/* Payment Settings Section */}
                                    <div className="col-12">
                                        <hr className="my-4" />
                                        <h6 className="mb-4">Payment Settings</h6>
                                    </div>

                                    {/* Mobile Payment */}
                                    <div className="col-sm-6">
                                        <div className="mb-3">
                                            <label className="form-label">Mobile Payment</label>
                                            <div className="d-flex align-items-center">
                                                <i
                                                    className={`ti ${!formData.mobilePayment
                                                        ? "ti-toggle-left text-danger"
                                                        : "ti-toggle-right text-primary"
                                                        }`}
                                                    style={{ fontSize: "34px", cursor: "pointer" }}
                                                    onClick={() => handleInputChange('mobilePayment', !formData.mobilePayment)}
                                                />
                                                <span className="ms-2">{formData.mobilePayment ? "Enabled" : "Disabled"}</span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Bank Payment */}
                                    <div className="col-sm-6">
                                        <div className="mb-3">
                                            <label className="form-label">Bank Payment</label>
                                            <div className="d-flex align-items-center">
                                                <i
                                                    className={`ti ${!formData.bankPayment
                                                        ? "ti-toggle-left text-danger"
                                                        : "ti-toggle-right text-primary"
                                                        }`}
                                                    style={{ fontSize: "34px", cursor: "pointer" }}
                                                    onClick={() => handleInputChange('bankPayment', !formData.bankPayment)}
                                                />
                                                <span className="ms-2">{formData.bankPayment ? "Enabled" : "Disabled"}</span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Mobile Payment Method (conditional) */}
                                    {formData.mobilePayment && (
                                        <div className="col-sm-6">
                                            <div className="mb-3">
                                                <label className="form-label">Mobile Payment Method<span className="text-danger">*</span></label>
                                                <select
                                                    className="form-control"
                                                    value={formData.paymentMethodId}
                                                    onChange={(e) => handleInputChange('paymentMethodId', e.target.value)}
                                                    disabled={loading}
                                                >
                                                    <option value="">-- Select Payment Method --</option>
                                                    {paymentSettings.map((setting, index) => (
                                                        <option key={index} value={setting._id}>
                                                            {setting.module} - {setting.shortCode}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                    )}

                                    {/* Bank Account Selection (conditional) */}
                                    {formData.bankPayment && (
                                        <div className="col-sm-6">
                                            <div className="mb-3">
                                                <label className="form-label">Bank Account<span className="text-danger">*</span></label>
                                                <select
                                                    className="form-control"
                                                    value={formData.bankAccountId}
                                                    onChange={(e) => handleInputChange('bankAccountId', e.target.value)}
                                                    disabled={loadingBankAccounts}
                                                >
                                                    <option value="">-- Select Bank Account --</option>
                                                    {bankAccounts.map((account) => (
                                                        <option key={account._id} value={account._id}>
                                                            {account.bankName} - {account.accountNumber} ({account.accountName})
                                                            {account.isDefault && ' (Default)'}
                                                        </option>
                                                    ))}
                                                </select>
                                                {loadingBankAccounts && <small className="text-muted">Loading bank accounts...</small>}
                                                {!loadingBankAccounts && bankAccounts.length === 0 && (
                                                    <small className="text-danger">No bank accounts found. Please add one in facility settings.</small>
                                                )}
                                            </div>
                                        </div>
                                    )}


                                    <div className="col-sm-6 mb-4">
                                        <label className="form-label">
                                            DigiTax Submission Profile
                                        </label>

                                        {digitaxProfiles.filter(profile => profile.enabled).length === 0 ? (
                                            <select
                                                className="form-select"
                                                value={formData.digitaxEnabled ? "enabled" : ""}
                                                onChange={(e) => {
                                                    const enabled = e.target.value === "enabled";

                                                    handleInputChange(
                                                        "digitaxEnabled",
                                                        enabled
                                                    );

                                                    handleInputChange(
                                                        "digitaxProfileId",
                                                        null
                                                    );
                                                }}
                                            >
                                                <option value="">
                                                    DigiTax Disabled
                                                </option>

                                                <option value="enabled">
                                                    Use Facility DigiTax Configuration
                                                </option>
                                            </select>
                                        ) : (
                                            <select
                                                className="form-select"
                                                value={formData.digitaxProfileId || ""}
                                                onChange={(e) => {
                                                    const profileId = e.target.value || null;

                                                    handleInputChange(
                                                        "digitaxProfileId",
                                                        profileId
                                                    );

                                                    handleInputChange(
                                                        "digitaxEnabled",
                                                        Boolean(profileId)
                                                    );
                                                }}
                                            >
                                                <option value="">
                                                    DigiTax Disabled
                                                </option>

                                                {digitaxProfiles
                                                    .filter(profile => profile.enabled)
                                                    .map(profile => (
                                                        <option
                                                            key={profile._id}
                                                            value={profile._id}
                                                        >
                                                            {profile.name}
                                                        </option>
                                                    ))}
                                            </select>
                                        )}
                                    </div>

                                    {/* Invoice GL Accounts */}
                                    {glAccounts.length > 0 && (
                                        <>
                                            <div className="col-12 mb-2">
                                                <h6>Invoice GL Entries</h6>
                                            </div>

                                            <div className="col-sm-6 mb-4">
                                                <label className="form-label mb-2">Invoice Debit Account<span className="text-danger">*</span></label>
                                                <select
                                                    className="form-control"
                                                    value={formData.glAccounts.invoice.debit}
                                                    onChange={(e) => handleInputChange('glAccounts.invoice.debit', e.target.value)}
                                                >
                                                    <option value="">-- Select Debit Account --</option>
                                                    {glAccounts.map((account) => (
                                                        <option key={account._id} value={account._id}>
                                                            {account.accountCode} - {account.accountName} ({account.accountType})
                                                        </option>
                                                    ))}
                                                </select>
                                                <small className="text-muted">Account to be debited when invoices are created</small>
                                            </div>

                                            <div className="col-sm-6 mb-4">
                                                <label className="form-label mb-2">Invoice Credit Account<span className="text-danger">*</span></label>
                                                <select
                                                    className="form-control"
                                                    value={formData.glAccounts.invoice.credit}
                                                    onChange={(e) => handleInputChange('glAccounts.invoice.credit', e.target.value)}
                                                >
                                                    <option value="">-- Select Credit Account --</option>
                                                    {glAccounts.map((account) => (
                                                        <option key={account._id} value={account._id}>
                                                            {account.accountCode} - {account.accountName} ({account.accountType})
                                                        </option>
                                                    ))}
                                                </select>
                                                <small className="text-muted">Account to be credited when invoices are created</small>
                                            </div>

                                            {/* Payment GL Accounts */}
                                            <div className="col-12 mb-2">
                                                <h6>Payment GL Entries</h6>
                                            </div>

                                            <div className="col-sm-6 mb-4">
                                                <label className="form-label mb-2">Payment Debit Account<span className="text-danger">*</span></label>
                                                <select
                                                    className="form-control"
                                                    value={formData.glAccounts.payment.debit}
                                                    onChange={(e) => handleInputChange('glAccounts.payment.debit', e.target.value)}
                                                >
                                                    <option value="">-- Select Debit Account --</option>
                                                    {glAccounts.map((account) => (
                                                        <option key={account._id} value={account._id}>
                                                            {account.accountCode} - {account.accountName} ({account.accountType})
                                                        </option>
                                                    ))}
                                                </select>
                                                <small className="text-muted">Account to be debited when payments are received</small>
                                            </div>

                                            <div className="col-sm-6 mb-4">
                                                <label className="form-label mb-2">Payment Credit Account<span className="text-danger">*</span></label>
                                                <select
                                                    className="form-control"
                                                    value={formData.glAccounts.payment.credit}
                                                    onChange={(e) => handleInputChange('glAccounts.payment.credit', e.target.value)}
                                                >
                                                    <option value="">-- Select Credit Account --</option>
                                                    {glAccounts.map((account) => (
                                                        <option key={account._id} value={account._id}>
                                                            {account.accountCode} - {account.accountName} ({account.accountType})
                                                        </option>
                                                    ))}
                                                </select>
                                                <small className="text-muted">Account to be credited when payments are received</small>
                                            </div>
                                        </>
                                    )}

                                    {/* Reminder Settings Section */}
                                    <div className="col-12">
                                        <hr className="my-4" />
                                        <h6 className="mb-4">Reminder Settings</h6>
                                    </div>

                                    <div className="col-sm-6">
                                        <div className="mb-3">
                                            <label className="form-label">Reminder Time</label>
                                            <input
                                                type="time"
                                                className="form-control"
                                                value={formData.reminder.time}
                                                onChange={(e) => handleInputChange('reminder.time', e.target.value)}
                                            />
                                            <small className="text-muted">Default: 9:00 AM</small>
                                        </div>
                                    </div>

                                    {/* Overdue Reminder Section */}
                                    <div className="col-sm-6">
                                        <div className="mb-3">
                                            <label className="form-label">Overdue Reminder</label>
                                            <div className="form-check mb-2">
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    id="overdueEnabled"
                                                    checked={formData.reminder.remindOn.afterOverdue.enabled}
                                                    onChange={(e) => handleInputChange('reminder.remindOn.afterOverdue.enabled', e.target.checked)}
                                                />
                                                <label className="form-check-label" htmlFor="overdueEnabled">
                                                    Enable Overdue Reminder
                                                </label>
                                            </div>

                                            {formData.reminder.remindOn.afterOverdue.enabled && (
                                                <>
                                                    <select
                                                        className="form-control mb-2 mt-2"
                                                        value={formData.reminder.remindOn.afterOverdue.daily ? 'daily' : 'specific'}
                                                        onChange={(e) => handleInputChange('reminder.remindOn.afterOverdue.mode', e.target.value)}
                                                    >
                                                        {OVERDUE_REMINDER_MODES.map(option => (
                                                            <option key={option.value} value={option.value}>
                                                                {option.label}
                                                            </option>
                                                        ))}
                                                    </select>

                                                    {formData.reminder.remindOn.afterOverdue.enabled && !formData.reminder.remindOn.afterOverdue.daily && (
                                                        <div className="mt-2">
                                                            <label className="form-label">Select days after due date</label>
                                                            <div className="d-flex flex-column gap-2">
                                                                {OVERDUE_REMINDER_OPTIONS.map(option => (
                                                                    <div key={option.value} className="form-check">
                                                                        <input
                                                                            type="checkbox"
                                                                            className="form-check-input"
                                                                            id={`overdue-day-${option.value}`}
                                                                            checked={formData.reminder.remindOn.afterOverdue.days.includes(option.value)}
                                                                            onChange={(e) => {
                                                                                const currentDays = [...formData.reminder.remindOn.afterOverdue.days];
                                                                                if (e.target.checked) {
                                                                                    if (!currentDays.includes(option.value)) {
                                                                                        currentDays.push(option.value);
                                                                                    }
                                                                                } else {
                                                                                    const index = currentDays.indexOf(option.value);
                                                                                    if (index !== -1) {
                                                                                        currentDays.splice(index, 1);
                                                                                    }
                                                                                }
                                                                                handleInputChange('reminder.remindOn.afterOverdue.days', currentDays);
                                                                            }}
                                                                        />
                                                                        <label className="form-check-label" htmlFor={`overdue-day-${option.value}`}>
                                                                            {option.label}
                                                                        </label>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}

                                                    {formData.reminder.remindOn.afterOverdue.enabled && formData.reminder.remindOn.afterOverdue.daily && (
                                                        <div className="mt-2">
                                                            <small className="text-muted">
                                                                A reminder will be sent every day after the due date until payment is made.
                                                            </small>
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </div>

                                    <div className="col-sm-6">
                                        <div className="mb-3">
                                            <label className="form-label">Send Reminders On</label>
                                            <div className="d-flex flex-column gap-2">
                                                <div className="form-check">
                                                    <input
                                                        type="checkbox"
                                                        className="form-check-input"
                                                        id="invoiceDate"
                                                        checked={formData.reminder.remindOn.invoiceDate}
                                                        onChange={(e) => handleInputChange('reminder.remindOn.invoiceDate', e.target.checked)}
                                                    />
                                                    <label className="form-check-label ms-2" htmlFor="invoiceDate">
                                                        Invoice Generation Date
                                                    </label>
                                                </div>
                                                <div className="form-check">
                                                    <input
                                                        type="checkbox"
                                                        className="form-check-input"
                                                        id="dueDate"
                                                        checked={formData.reminder.remindOn.dueDate}
                                                        onChange={(e) => handleInputChange('reminder.remindOn.dueDate', e.target.checked)}
                                                    />
                                                    <label className="form-check-label ms-2" htmlFor="dueDate">
                                                        Due Date
                                                    </label>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="col-sm-6">
                                        <div className="mb-3">
                                            <label className="form-label">Notification Methods</label>
                                            <div className="d-flex gap-3">
                                                {NOTIFICATION_TYPES.map(type => (
                                                    <div key={type.value} className="form-check">
                                                        <input
                                                            type="checkbox"
                                                            className="form-check-input"
                                                            id={`notification-${type.value}`}
                                                            checked={formData.reminder.notificationTypes.includes(type.value)}
                                                            onChange={() => handleNotificationTypeChange(type.value)}
                                                        />
                                                        <label className="form-check-label ms-2" htmlFor={`notification-${type.value}`}>
                                                            {type.label}
                                                        </label>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="col-12">
                                        <div className="mb-3">
                                            <label className="form-label">Reminder Message (Optional)</label>
                                            <textarea
                                                className="form-control"
                                                rows="3"
                                                value={formData.reminder.message || ''}
                                                onChange={(e) => handleInputChange('reminder.message', e.target.value)}
                                                maxLength="500"
                                            ></textarea>
                                            <small className="text-muted">
                                                Maximum 500 characters. {formData.reminder.message ? `${500 - formData.reminder.message.length} characters remaining` : ''}
                                            </small>
                                        </div>
                                    </div>

                                    {/* Penalty Settings Section */}
                                    <div className="col-12">
                                        <hr className="my-4" />
                                        <h6 className="mb-4">Penalty Settings (Optional)</h6>
                                    </div>

                                    <div className="col-sm-6">
                                        <div className="mb-3">
                                            <label className="form-label">Penalty Category</label>
                                            <select
                                                className="form-control"
                                                value={formData.penalty.category}
                                                onChange={(e) => handleInputChange('penalty.category', e.target.value)}
                                            >
                                                <option value="">-- Select Category --</option>
                                                {PENALTY_CATEGORY_OPTIONS.map(option => (
                                                    <option key={option.value} value={option.value}>
                                                        {option.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>

                                    {formData.penalty.category && (
                                        <div className="col-sm-6">
                                            <div className="mb-3">
                                                <label className="form-label">Penalty Name<span className="text-danger">*</span></label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    value={formData.penalty.name}
                                                    onChange={(e) => handleInputChange('penalty.name', e.target.value)}
                                                    placeholder="e.g., Late Payment Penalty"
                                                />
                                            </div>
                                        </div>
                                    )}

                                    {formData.penalty.category && (
                                        <div className="col-sm-6">
                                            <div className="mb-3">
                                                <label className="form-label">Effect Days<span className="text-danger">*</span></label>
                                                <input
                                                    type="number"
                                                    className="form-control"
                                                    value={formData.penalty.effectDays}
                                                    onChange={(e) => handleInputChange('penalty.effectDays', e.target.value)}
                                                    placeholder="Days after due date"
                                                />
                                            </div>
                                        </div>
                                    )}

                                    {/* Financial Penalty Inputs */}
                                    {showFinancialPenaltyInputs() && (
                                        <>
                                            <div className="col-12 mb-2">
                                                <h6 className="text-warning">Financial Penalty</h6>
                                            </div>

                                            <div className="col-sm-6">
                                                <div className="mb-3">
                                                    <label className="form-label">Penalty Type<span className="text-danger">*</span></label>
                                                    <select
                                                        className="form-control"
                                                        value={formData.penalty.type}
                                                        onChange={(e) => handleInputChange('penalty.type', e.target.value)}
                                                    >
                                                        <option value="">-- Select Type --</option>
                                                        <option value="fixed">Fixed Amount</option>
                                                        <option value="percentage">Percentage</option>
                                                    </select>
                                                </div>
                                            </div>

                                            {formData.penalty.type === 'fixed' && (
                                                <div className="col-sm-6">
                                                    <div className="mb-3">
                                                        <label className="form-label">Penalty Amount<span className="text-danger">*</span></label>
                                                        <input
                                                            type="number"
                                                            className="form-control"
                                                            value={formData.penalty.amount}
                                                            onChange={(e) => handleInputChange('penalty.amount', e.target.value)}
                                                            placeholder="Fixed penalty amount"
                                                        />
                                                    </div>
                                                </div>
                                            )}

                                            {formData.penalty.type === 'percentage' && (
                                                <div className="col-sm-6">
                                                    <div className="mb-3">
                                                        <label className="form-label">Penalty Percentage<span className="text-danger">*</span></label>
                                                        <input
                                                            type="number"
                                                            className="form-control"
                                                            value={formData.penalty.percentage}
                                                            onChange={(e) => handleInputChange('penalty.percentage', e.target.value)}
                                                            placeholder="Percentage (e.g., 5 for 5%)"
                                                            min="0"
                                                            max="100"
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                        </>
                                    )}

                                    {/* Enforcement Penalty Inputs */}
                                    {showEnforcementPenaltyInputs() && (
                                        <>
                                            <div className="col-12 mb-2">
                                                <h6 className="text-danger">Enforcement Action</h6>
                                            </div>

                                            <div className="col-sm-6">
                                                <div className="mb-3">
                                                    <label className="form-label">Enforcement Type<span className="text-danger">*</span></label>
                                                    <select
                                                        className="form-control"
                                                        value={formData.penalty.enforcementType}
                                                        onChange={(e) => handleInputChange('penalty.enforcementType', e.target.value)}
                                                    >
                                                        <option value="">-- Select Enforcement --</option>
                                                        {ENFORCEMENT_TYPE_OPTIONS.map(option => (
                                                            <option key={option.value} value={option.value}>
                                                                {option.label}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                            </div>
                                        </>
                                    )}

                                    <div className="col-12">
                                        <div className="text-end">
                                            <Button
                                                type="button"
                                                className="btn btn-outline-secondary me-2"
                                                onClick={clearForm}
                                                disabled={loading}
                                            >
                                                Clear
                                            </Button>
                                            <Button
                                                type="button"
                                                className="btn"
                                                style={{ backgroundColor: 'limegreen', color: 'white', border: 'none' }}
                                                onClick={handleLevySubmit}
                                                disabled={loading}
                                            >
                                                {loading ? 'Submitting...' : 'Submit'}
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
}

export default AddLevy;